import React from 'react';

interface TableSection {
  title: string;
  rows: { label: string; value: string }[];
}

const parseTableSections = (jsonString: string): TableSection[] => {
  try {
    const data = JSON.parse(jsonString);

    const sections: TableSection[] = [];

    // Process column differences
    if (data.column_differences) {
      sections.push({
        title: 'Column Differences',
        rows: [
          {
            label: 'Columns in new version but not in old version',
            value: JSON.stringify(data.column_differences.new_only),
          },
          {
            label: 'Columns in old version but not in new version',
            value: JSON.stringify(data.column_differences.old_only),
          },
        ],
      });
    }

    // Process employee data stats
    if (data.employee_data?.stats) {
      const employeeStats = data.employee_data.stats;
      sections.push({
        title: 'Employee Data Stats',
        rows: Object.entries(employeeStats).map(([key, value]) => ({
          label: key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase()),
          value: JSON.stringify(value),
        })),
      });
    }

    // Process employee data differences
    if (data.employee_data?.differences) {
      const employeeDifferences = data.employee_data.differences;
      const differenceRows = Object.entries(employeeDifferences)
        .filter(([, diffs]) => Array.isArray(diffs) && diffs.length > 0) // Only include non-empty differences
        .map(([column, diffs]) => ({
          label: `Differences in ${column}`,
          value: JSON.stringify(diffs),
        }));

      sections.push({
        title: 'Employee Data Differences',
        rows:
          differenceRows.length > 0
            ? differenceRows
            : [{ label: 'No differences', value: '' }],
      });
    }

    // Process applicant data stats
    if (data.applicant_data?.stats) {
      const applicantStats = data.applicant_data.stats;
      sections.push({
        title: 'Applicant Data Stats',
        rows: Object.entries(applicantStats).map(([key, value]) => ({
          label: key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase()),
          value: JSON.stringify(value),
        })),
      });
    }

    // Process applicant data differences
    if (data.applicant_data?.differences) {
      const applicantDifferences = data.applicant_data.differences;
      const differenceRows = Object.entries(applicantDifferences)
        .filter(([, diffs]) => Array.isArray(diffs) && diffs.length > 0) // Only include non-empty differences
        .map(([column, diffs]) => ({
          label: `Differences in ${column}`,
          value: JSON.stringify(diffs),
        }));

      sections.push({
        title: 'Applicant Data Differences',
        rows:
          differenceRows.length > 0
            ? differenceRows
            : [{ label: 'No differences', value: '' }],
      });
    }

    return sections;
  } catch (e) {
    return [];
  }
};

export const TransformComparisonTable = ({
  rawOutput,
}: {
  rawOutput: string;
}) => {
  if (!rawOutput) {
    return <div>No comparison data available.</div>;
  }

  const data = parseTableSections(rawOutput);

  return (
    <div className="overflow-x-auto">
      {data.map((section, sectionIndex) => (
        <div key={sectionIndex} className="mb-8">
          <h2 className="text-lg font-bold mb-4">{section.title}</h2>
          <table className="table-auto border-collapse border border-gray-300 w-full">
            <thead>
              <tr>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Metric
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Value
                </th>
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td className="border border-gray-300 px-4 py-2">
                    {row.label}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {row.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default TransformComparisonTable;
